import { useEffect, useState, type MouseEvent, type ReactNode } from 'react';
import { legacyTimestamp } from '../generateReport';
import {
  fetchCollection,
  formatInputValue,
  formatSpanValue,
  hbIf,
  hbStr,
  ifCompare,
  type FormatterMode,
} from '../reportTabData';
import type { GeneratedTabPaneProps } from './GeneratedReportTabs';

/**
 * Phase 5d — the "ER Wheel Selection" tab (tabs/ewheel/view/
 * er_wheel_selection.js + tmpl/ewheel.selector.tmpl + tmpl/report.ahu.tmpl).
 * What the legacy tab REALLY does:
 *
 *  - 'generate:report:er_wheel_selection' (er_wheel_selection.js:145-166):
 *    render() the static shell SYNCHRONOUSLY at dispatch (:147-149 — NO
 *    generate POST; the kit rightly has no REPORT_DISPATCH entry), then GET
 *    collection.energy_wheel_selection
 *    (`/api/v1.0/reports/energy_wheel_selection/<ahu_id>`, collection/all.js)
 *    and the CMS notes (`/api/v1.0/cms/1/v1.0.0/ahu/energy_wheels/notes/`,
 *    collection/notes.js), then renderReport into `.content-item` (:102-143).
 *  - renderReport: splits models[0].rating_notes ("k:v;k:v") into an object,
 *    filtering 'null' values (:110-129); attaches cms_notes to models[0]
 *    ONLY (:130); renders report.ahu.tmpl; the `.integer` loop (:132-137) is
 *    a no-op (no .integer elements in the template); numberFormatting (:58-68)
 *    binds fn.uni_tools.bindFormatter to every input/span carrying
 *    data-formatter_type (comma grouping, decimal→2dp, integer→truncate) —
 *    reproduced at render time via formatSpanValue/formatInputValue;
 *    setTimeStamp (:139,212-214) fills the header span; the #print/#export_csv
 *    disabling (:140-141) targets buttons the header no longer renders — no-op.
 *
 *  Dead legacy code, deliberately NOT ported:
 *  - 'open:generated_report:er_wheel_selection' re-render is fully commented
 *    out (:168-177) → generated stays false and re-opening the tab shows the
 *    empty board again (unlike phx_selection/ahu_model) — so this tab is NOT
 *    in REOPENABLE_TABS and only renders in generate mode.
 *  - events 'click #notes' → showNotes (:24): the method does not exist on
 *    the view — Backbone delegateEvents silently skips missing methods.
 *  - reportTmplAhuPrint is compiled (:36) but never invoked — no print
 *    listener is registered for this tab (initialize :32-38).
 *  - EMPTY collection: renderReport reads models[0].rating_notes BEFORE the
 *    html() insert (:111) → TypeError for an AHU without an ER wheel, so the
 *    "No Coil section in Air Handling Unit" note (report.ahu.tmpl:1-3) is
 *    unreachable and the dotted preloader stays forever. Reproduced (minus
 *    the console error) by keeping the preloader when models is empty.
 *
 *  Interaction quirk documented, approximated: activateLabel (:93-100) keys
 *  off the clicked li's FIRST class token, so clicking the initially-selected
 *  Properties tab (whose first token is 'selected') clears every 'selected'
 *  highlight once. Here 'selected' simply follows the active tab.
 */

type Row = Record<string, unknown>;

interface NoteRow {
  note?: string;
}

interface EwheelResult {
  models: Row[];
  /** models[0].rating_notes after the :110-129 transform — an object of
   *  {label: text}, or the raw string when every part was 'null'. */
  ratingNotes: Record<string, string | undefined> | string | null;
  cmsNotes: NoteRow[];
  timeStamp: string;
}

const sub = (m: Row, key: string): Row => ((m[key] ?? {}) as Row);
const at0 = (v: unknown): Row => ((Array.isArray(v) ? v[0] : undefined) ?? {}) as Row;

/** er_wheel_selection.js:110-129 — rating_notes "k:v;k:v" → {k: v}. */
function splitRatingNotes(raw: unknown): Record<string, string | undefined> | string | null {
  const splitedText = raw ? String(raw).split(';') : [];
  if (!splitedText.length) return (raw as string | null) ?? null;
  const splitedNotes: Record<string, string | undefined> = {};
  let assigned = false;
  for (const piece of splitedText) {
    if (piece) {
      const splitedFirst = piece.split(':');
      // legacy loops the split parts but the body only ever runs the same
      // assignment (:121-125)
      if (splitedFirst[1] !== 'null') {
        splitedNotes[splitedFirst[0]] = splitedFirst[1];
        assigned = true;
      }
    }
  }
  // When nothing qualified, models[0].rating_notes keeps the RAW string
  // (truthy → the Warning header renders with no items, like the legacy
  // {{#each}} over a string).
  return assigned ? splitedNotes : ((raw as string | null) ?? null);
}

/** Readonly text input (the `{{#unless header}}` branch — header is never
 *  set by the live view, report.ahu.tmpl:38-39). */
const ROText = ({
  className,
  value,
  fmt,
  extra,
}: {
  className: string;
  value: unknown;
  fmt?: FormatterMode;
  extra?: Record<string, string>;
}) => (
  <input
    className={className}
    type="text"
    readOnly
    tabIndex={-1}
    value={fmt ? formatInputValue(value, fmt) : hbStr(value)}
    data-formatter_type={fmt}
    {...extra}
  />
);

const SEARCH_FIELD = 'search_field required_field project_field has_default';

/** A number_output span; fmt present ⇒ data-formatter_type + bindFormatter
 *  initial formatting; absent ⇒ raw text (numberFormatting only binds
 *  elements carrying the attribute, er_wheel_selection.js:63-66). */
const NumOut = ({
  value,
  fmt,
  name,
  cls = 'number_output',
}: {
  value: unknown;
  fmt?: FormatterMode;
  name: string;
  cls?: string;
}) => (
  <span className={cls} data-formatter_type={fmt} data-name={name}>
    {fmt ? formatSpanValue(value, fmt) : hbStr(value)}
  </span>
);

/** Output row scaffolding used across the Performance output columns. */
const OutRow = ({ label, children }: { label: ReactNode; children: ReactNode }) => (
  <div className="row">
    <div className="col-xs-4 label-part">{label}</div>
    <div className="col-xs-8 input-part">
      <div className="row">{children}</div>
    </div>
  </div>
);

const stop = (e: MouseEvent) => e.preventDefault();

const EwheelSelectionTab = ({ report, states }: GeneratedTabPaneProps) => {
  const [result, setResult] = useState<EwheelResult | null>(null);
  // Input/output tab selection per bootstrap-tab behavior (bootstrap 3.3.4 is
  // loaded by the legacy page, report app.js:453; anchors carry
  // data-toggle="tab"). Initial: Properties panes 'in active'.
  const [inputTab, setInputTab] = useState('Properties');
  // Bootstrap only stamps li.active on the first click — the initial lis
  // carry 'selected' alone (report.ahu.tmpl:12,672).
  const [inputClicked, setInputClicked] = useState(false);
  const [outputTab, setOutputTab] = useState('Properties_output');
  const [outputClicked, setOutputClicked] = useState(false);
  // summerWinterTab (:70-91) toggles every .Performance .tab-pane globally —
  // one shared season for the input AND output groups. Default: summer
  // (report.ahu.tmpl:299,305,739,744).
  const [season, setSeason] = useState<'summer' | 'winter'>('summer');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const state of states ?? []) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const models = await fetchCollection<Row[]>(
            `/api/v1.0/reports/energy_wheel_selection/${state.ahu_id}`
          );
          if (!Array.isArray(models) || !models.length) {
            // legacy renderReport TypeError at :111 — preloader stays.
            continue;
          }
          // eslint-disable-next-line no-await-in-loop
          const notes = await fetchCollection<NoteRow[]>(
            '/api/v1.0/cms/1/v1.0.0/ahu/energy_wheels/notes/'
          );
          if (cancelled) return;
          setResult({
            models,
            ratingNotes: splitRatingNotes(models[0]?.rating_notes),
            cmsNotes: Array.isArray(notes) ? notes : [],
            timeStamp: legacyTimestamp(),
          });
        } catch {
          // Legacy fetch failure: the .then chain never runs — preloader stays.
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [states]);

  const inputLi = (p: string, name: string) => {
    const sel = inputTab === name;
    return (
      <li className={`${sel ? 'selected ' : ''}${p}${name} input_label${sel && inputClicked ? ' active' : ''}`}>
        <a
          tabIndex={-1}
          data-toggle="tab"
          href={`#${p}${name}`}
          onClick={(e) => {
            stop(e);
            setInputTab(name);
            setInputClicked(true);
          }}
        >
          {name}
        </a>
      </li>
    );
  };

  const paneCls = (name: string, extra: string) =>
    `tab-pane fade${inputTab === name ? ' in active' : ''} ${extra}`;

  const seasonLi = (tab: 'summer' | 'winter') => (
    <li className={`${season === tab ? 'active ' : ''}${tab}-tab`}>
      <a
        tabIndex={-1}
        data-toggle="tab"
        data-tab={tab}
        onClick={(e) => {
          stop(e);
          setSeason(tab);
        }}
      >
        {/* tmpl:299-300 — '  summer &#160;  ' / '  winter ' */}
        {tab === 'summer' ? <>{'  summer '}&#160;{'  '}</> : '  winter '}
      </a>
    </li>
  );

  return (
    <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
      {/* ewheel.selector.tmpl:1-14 — tooltip WITHOUT leading space; the
          time-stamp starts with the hardcoded 2017 placeholder and is only
          replaced by setTimeStamp after renderReport (:139). */}
      <div className="container-header clearfix">
        <h3 className="left">
          <span className="group-name" tooltip={`: ${report.name}`}>
            Engineering
          </span>
          <span className="report-name">{report.name}</span>
          <span className="time-stamp">
            {result ? `Generated on: ${result.timeStamp}` : 'Generated on: 2017.09.13 19:10:46'}
          </span>
        </h3>
      </div>
      <div className="col-xs-12 ewheel-data-content">
        <div className="content">
          <div className="content-item content-with-page-navbar">
            {result ? (
              result.models.map((m, idx) => (
                <EwheelModel
                  key={idx}
                  m={m}
                  isFirst={idx === 0}
                  result={result}
                  inputLi={inputLi}
                  paneCls={paneCls}
                  seasonLi={seasonLi}
                  season={season}
                  outputTab={outputTab}
                  outputClicked={outputClicked}
                  onOutputTab={(t) => {
                    setOutputTab(t);
                    setOutputClicked(true);
                  }}
                />
              ))
            ) : (
              <div className="preloader dotted-preloader active" />
            )}
          </div>
        </div>
      </div>
      <div className="ewheel-data-print" />
    </div>
  );
};

interface ModelProps {
  m: Row;
  isFirst: boolean;
  result: EwheelResult;
  inputLi: (p: string, name: string) => ReactNode;
  paneCls: (name: string, extra: string) => string;
  seasonLi: (tab: 'summer' | 'winter') => ReactNode;
  season: 'summer' | 'winter';
  outputTab: string;
  outputClicked: boolean;
  onOutputTab: (t: string) => void;
}

/** One `{{#each models}}` block of tabs/ewheel/tmpl/report.ahu.tmpl:4-1664.
 *  `prefix` is not a serializer field, so ids/hrefs render with the bare
 *  names, exactly like the live board. */
const EwheelModel = ({
  m,
  isFirst,
  result,
  inputLi,
  paneCls,
  seasonLi,
  season,
  outputTab,
  outputClicked,
  onOutputTab,
}: ModelProps) => {
  const p = hbStr(m.prefix);
  const mw = sub(m, 'master_energy_wheel');
  // rating notes / cms notes only exist on models[0] (view :110-130).
  const ratingNotes = isFirst ? result.ratingNotes : ((m.rating_notes as string | null) ?? null);
  const cmsNotes = isFirst ? result.cmsNotes : [];
  const ratingNotesIsObject = typeof ratingNotes === 'object' && ratingNotes !== null;
  const notFound = hbIf(m.notFound);
  const nonClickableOk = ifCompare(m.ok, '==', false); // {{#ifCompare ok '==' false}}
  const warnNotes = hbIf((m as Row).splitedNotes) || ifCompare(m.ok, '==', 'false'); // tmpl:679

  const outputLi = (name: string, label: string, extraCls: string) => {
    const sel = outputTab === name;
    return (
      <li
        data-tab={`${p}${name}`}
        className={`${p}${label}-output output_label${sel ? ' selected' : ''}${sel && outputClicked ? ' active' : ''}${extraCls}`}
      >
        <a
          className={label === 'Notes' && warnNotes ? 'warning-notes' : undefined}
          data-toggle="tab"
          href={`#${p}${name}`}
          onClick={(e) => {
            stop(e);
            onOutputTab(name);
          }}
        >
          {label}
        </a>
      </li>
    );
  };
  const outPaneCls = (name: string, extra: string) =>
    `tab-pane fade${outputTab === name ? ' in active' : ''} ${extra}`;

  const summerActive = season === 'summer';

  return (
    <>
      <div id="er-wheel" className="coil_new input-parts er-wheel coil-rating-mode rating">
        <div className="col-xs-1 row nav nav-tabs">
          <ul className="col-xs-12">
            <li>Input</li>
            {inputLi(p, 'Project')}
            {inputLi(p, 'Properties')}
            {inputLi(p, 'Performance')}
            {inputLi(p, 'Options')}
            {inputLi(p, 'Pricing')}
          </ul>
        </div>

        <div
          className="col-xs-11 content-data tab-content"
          style={{ paddingTop: 18, paddingBottom: 25 }}
        >
          <div className="row">
            {/* ---- Input · Project (tmpl:29-88) ---- */}
            <div className={paneCls('Project', 'scrollable-content col-xs-12 float-left')} id={`${p}Project`}>
              <div className="col-xs-12 col-md-12 col-lg-6">
                <div className="row">
                  <div className="col-xs-6 label-part">Project Name</div>
                  <div className="col-xs-6 input-part">
                    <div className="row">
                      <div className="col-xs-12">
                        <ROText className="" value={m.project_name} />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-6 label-part">AHU</div>
                  <div className="col-xs-6 input-part">
                    <div className="row">
                      <div className="col-xs-12">
                        {/* {{ahu.tag}} — the serializer only exposes the flat
                            ahu_tag, so this is empty on the live board too. */}
                        <ROText className="" value={sub(m, 'ahu').tag} />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-6 label-part">Section</div>
                  <div className="col-xs-6 input-part">
                    <div className="row">
                      <div className="col-xs-12">
                        {/* {{section.tag}} — same: empty (flat section_tag only). */}
                        <ROText className="" value={sub(m, 'section').tag} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xs-12 col-md-12 col-lg-6 project-group">
                <div className="row">
                  <div className="col-xs-5 label-part">
                    <span data-name="elevation">Altitude</span>
                    <span className="percent right">ft </span>
                  </div>
                  <div className="col-xs-7 input-part">
                    <div className="row">
                      <div className="col-xs-12">
                        {/* {{elevation}} — serializer field is `altitude`;
                            empty on the live board. */}
                        <input
                          className={SEARCH_FIELD}
                          autoComplete="off"
                          type="text"
                          data-type="number"
                          readOnly
                          tabIndex={-1}
                          data-min="0"
                          data-step="1"
                          data-max="15000"
                          name="elevation"
                          data-name="altitude"
                          value={hbStr(m.elevation)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ---- Input · Properties (tmpl:89-294) ---- */}
            <div className={paneCls('Properties', 'scrollable-content col-xs-12 float-left')} id={`${p}Properties`}>
              <div className="col-xs-12 col-md-12 col-lg-6">
                {(
                  [
                    ['mfg_id', 'Manufacturer', m.er_wheel_mfg],
                    ['series_id', 'Series', mw.series],
                    ['type_id', 'Type', mw.type],
                    ['desiccant_id', 'Desiccant', mw.desiccant],
                    ['wheel_size_id', 'Wheel Size', mw.wheel_size],
                  ] as Array<[string, string, unknown]>
                ).map(([name, label, value]) => (
                  <div className="row" key={name}>
                    <span className="col-xs-6 label-part" data-name={name}>
                      {label}
                    </span>
                    <div className="col-xs-6 input-part">
                      <div className="row">
                        <div className={name === 'wheel_size_id' ? 'col-xs-12 qty-fl-select' : 'col-xs-12'}>
                          <ROText className={SEARCH_FIELD} value={value} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="row">
                  <span className="col-xs-6 label-part" data-name="purge_angle_id">
                    Purge
                    <span className="percent right">deg</span>
                  </span>
                  <div className="col-xs-6 input-part">
                    <div className="row">
                      <div className="col-xs-12">
                        <input
                          className={SEARCH_FIELD}
                          type="text"
                          data-type="number"
                          readOnly
                          tabIndex={-1}
                          value={hbStr(mw.purge_angle)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <span className="col-xs-6 label-part" data-name="fin_pattern_id">
                    Fin Pattern
                  </span>
                  <div className="col-xs-6 input-part">
                    <div className="row">
                      <div className="col-xs-12">
                        <ROText className={SEARCH_FIELD} value={mw.fin_pattern} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xs-12 col-md-12 col-lg-6 performance-group">
                {(
                  [
                    ['mounting_arr_id', 'Mounting Arrangement ', null, mw.mounting_arr],
                    ['speed_control_id', 'Speed Control', null, mw.speed_control],
                    ['speed', 'Speed', 'RPM', mw.speed],
                    ['mtr_voltage_id', 'Motor Voltage', 'V/Fr/Ph', mw.mtr_voltage],
                    ['fan_locations_id', 'Fan Location', null, m.fan_locations],
                    ['flow_type_id', 'Flow', null, m.flow_type],
                    ['certification_id', 'Certification', null, at0(mw.certification).name],
                  ] as Array<[string, string, string | null, unknown]>
                ).map(([name, label, unit, value]) => (
                  <div className="row" key={name}>
                    <div className="col-xs-6 label-part">
                      <span data-name={name}>{label}</span>
                      {name === 'speed' && ' --'}
                      {unit && <span className="percent right">{unit}</span>}
                    </div>
                    <div className="col-xs-6 input-part">
                      <div className="row">
                        <div className="col-xs-12">
                          <ROText className={SEARCH_FIELD} value={value} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ---- Input · Performance (tmpl:295-570) ---- */}
            <div className={paneCls('Performance', 'scrollable-content col-xs-12 float-left Performance')} id={`${p}Performance`}>
              <div className="col-xs-12">
                <div className="row">
                  <ul className="col-xs-12 text-right summer-winter-tabing">
                    {seasonLi('summer')}
                    {seasonLi('winter')}
                  </ul>
                </div>
              </div>
              <div className="col-xs-12 col-lg-7 tab-content">
                <div id="summer" className={`summer tab-pane fade${summerActive ? ' in active' : ''}`}>
                  <SeasonInputBlock m={m} season="summer" />
                </div>
                <div id="winter" className={`winter tab-pane fade${!summerActive ? ' in active' : ''}`}>
                  <SeasonInputBlock m={m} season="winter" />
                </div>
              </div>
            </div>

            {/* ---- Input · Options (tmpl:571-622) ---- */}
            <div className={paneCls('Options', 'scrollable-content col-xs-12 float-left')} id={`${p}Options`}>
              <div className="col-xs-12 col-md-12 col-lg-6">
                {(
                  [
                    ['temp_control_id', 'Temperature Control', mw.temp_control],
                    ['rotation_detector_id', 'Rotation Detector', mw.rotation_detector],
                    ['coating_frame_id', ' Frame Coating', mw.coating_frame],
                    ['coating_media_id', 'Media Coating', mw.coating_media],
                  ] as Array<[string, string, unknown]>
                ).map(([name, label, value]) => (
                  <div className="row" key={name}>
                    <div className="col-xs-6 label-part">
                      <span data-name={name}>{label}</span>
                    </div>
                    <div className="col-xs-6 input-part">
                      <div className="row">
                        <input
                          className={SEARCH_FIELD}
                          type="text"
                          data-type="number"
                          readOnly
                          tabIndex={-1}
                          value={hbStr(value)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ---- Input · Pricing (tmpl:623-664) ---- */}
            <div className={paneCls('Pricing', 'scrollable-content col-xs-12')} id={`${p}Pricing`}>
              <div className="col-xs-12 col-md-12 col-lg-6 float-left">
                <div className="row">
                  <div className="col-xs-6 label-part">
                    <span data-name="pricing_method_id">Method-</span>
                  </div>
                  <div className="col-xs-6 input-part">
                    <div className="row">
                      <div className="col-xs-12 right text-right">
                        {/* legacy template renders coating_media here (tmpl:634) */}
                        <input
                          className={SEARCH_FIELD}
                          type="text"
                          data-type="number"
                          readOnly
                          tabIndex={-1}
                          value={hbStr(mw.coating_media)}
                          style={{ width: '88%' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row pricingInputs">
                  <div className={`col-xs-6 label-part ${hbStr(m.pricing_method_id)}`}>
                    {ifCompare(m.pricing_method_id, '==', 2) ? (
                      <span data-name="cost_product_user" data-name_2="cost_freight_user">
                        ER Wheel : Mtl | Freight-
                      </span>
                    ) : (
                      <span data-name="net_price_product_user" data-name_2="net_cost_freight_user">
                        ER Wheel : Mtl | Freight-
                      </span>
                    )}
                  </div>
                  <div className="col-xs-6 input-part four-input-row">
                    <div className="row">
                      <div className="col-xs-6">
                        <span className="percent ">US$</span>
                        <input
                          type="text"
                          data-formatter_type="decimal"
                          autoComplete="off"
                          readOnly
                          tabIndex={-1}
                          className="model_field coil_field"
                          name={ifCompare(m.pricing_method_id, '==', 2) ? 'cost_product_user' : 'net_price_product_user'}
                          value={formatInputValue(
                            ifCompare(m.pricing_method_id, '==', 2) ? m.cost_product_user : m.net_price_product_user,
                            'decimal'
                          )}
                        />
                      </div>
                      <div className="col-xs-6">
                        <input
                          type="text"
                          data-formatter_type="decimal"
                          autoComplete="off"
                          readOnly
                          tabIndex={-1}
                          className="model_field coil_field"
                          name={ifCompare(m.pricing_method_id, '==', 2) ? 'cost_freight_user' : 'net_cost_freight_user'}
                          value={formatInputValue(
                            ifCompare(m.pricing_method_id, '==', 2) ? m.cost_freight_user : m.net_cost_freight_user,
                            'decimal'
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ---- Output part (tmpl:668-1663) ---- */}
      <div className="  coil_new output-part rated-item output-group output-content rating">
        <div className="col-xs-1 nav nav-tabs">
          <ul className="ouptut_tubs coil-raiting">
            <li>Output</li>
            {outputLi('Properties_output', 'Properties', nonClickableOk ? ' non-clickable ' : '')}
            {outputLi('Performance_output', 'Performance', nonClickableOk ? ' non-clickable ' : '')}
            {outputLi('Notes-output', 'Notes', '')}
          </ul>
        </div>
        <div className="col-xs-11 with-border tab-content" style={{ float: 'right' }}>
          <div className="with-bottom-distance row">
            <div className="col-xs-12 content-data">
              <div className="row full-height">
                {notFound ? (
                  <div className="error-message">
                    Unable to find a coil that satisfies all inputted parameters.
                  </div>
                ) : (
                  <>
                    {/* Output · Properties (tmpl:692-731) */}
                    <div
                      className={outPaneCls('Properties_output', 'scrollable-content col-xs-12 float-left output-height')}
                      id={`${p}Properties_output`}
                    >
                      <div className="col-xs-12 col-md-12 col-lg-6" style={{ marginTop: 19 }}>
                        <div className="row">
                          <div className="col-xs-6 label-part">Model</div>
                          <div className="col-xs-6 input-part">
                            <div className="row">
                              <div className="col-xs-3 output-data">
                                <div className="row">
                                  <div className="output-without-unit" data-name="model">
                                    {hbStr(mw.model)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-xs-6 label-part">Weight</div>
                          <div className="col-xs-6 input-part">
                            <div className="row">
                              <div className="col-xs-3 output-data">
                                <div className="row">
                                  <span className="percent">lb</span>
                                  <NumOut value={mw.shipping_weight} fmt="integer" name="shipping_weight" />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Output · Performance (tmpl:732-1609) */}
                    <div
                      className={outPaneCls('Performance_output', 'scrollable-content col-xs-12 float-left output-height Performance')}
                      id={`${p}Performance_output`}
                    >
                      <div className="col-xs-12">
                        <div className="row">
                          <ul className="col-xs-12 text-right summer-winter-tabing">
                            {seasonLi('summer')}
                            {seasonLi('winter')}
                          </ul>
                        </div>
                      </div>
                      <div id="summer-o" className={`summer tab-pane fade${summerActive ? ' in active' : ''}`}>
                        <SeasonOutputBlock m={m} mw={mw} season="summer" />
                      </div>
                      <div id="winter-o" className={`winter tab-pane fade${!summerActive ? ' in active' : ''}`}>
                        <SeasonOutputBlock m={m} mw={mw} season="winter" />
                      </div>
                    </div>

                    {/* Output · Notes (tmpl:1611-1653) */}
                    <div
                      className={outPaneCls('Notes-output', 'scrollable-content col-xs-12 float-left output-height')}
                      id={`${p}Notes-output`}
                    >
                      <div className="col-xs-12 col-md-12 full-height">
                        <div className="row">
                          <div className="col-xs-12">
                            <div className="clearfix row nots_and_errors">
                              {hbIf(ratingNotes) && (
                                <div className="col-xs-12">
                                  <div className="row">
                                    <div className="col-xs-2">
                                      <p className="warning-notes">Warning : </p>
                                    </div>
                                    <div className="col-xs-10">
                                      {ratingNotesIsObject &&
                                        Object.entries(ratingNotes as Record<string, string | undefined>).map(
                                          ([key, value]) =>
                                            // {{#if @this}} — falsy values render nothing
                                            hbIf(value) ? (
                                              <p className="warning-notes label" key={key}>
                                                {' '}
                                                {key} : {value}
                                              </p>
                                            ) : null
                                        )}
                                    </div>
                                  </div>
                                </div>
                              )}
                              {hbIf(cmsNotes) && (
                                <div className="col-xs-12">
                                  <div className="row">
                                    <div className="col-xs-2">
                                      <p className="label">Mfg&apos;s Notes :</p>
                                    </div>
                                    <div className="col-xs-10">
                                      {cmsNotes.map((n, i) => (
                                        <p className=" label" key={i}>
                                          {hbStr(n.note)}
                                        </p>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="col-xs-11 col-xs-offset-1 ">
          <div className="row rating-time">
            Rated on :{' '}
            {hbIf(sub(m, 'rated_erwheel').rated_datetime)
              ? ` ${hbStr(sub(m, 'rated_erwheel').rated_datetime)} `
              : ` ${hbStr(m.rated_datetime)} `}
          </div>
        </div>
      </div>
    </>
  );
};

/** Input Performance season block (tmpl:305-434 summer / 435-568 winter). */
const SeasonInputBlock = ({ m, season }: { m: Row; season: 'summer' | 'winter' }) => {
  const s = season;
  const w = season === 'winter';
  return (
    <>
      <div className="row">
        <div className="col-xs-offset-4 col-xs-8 input-part">
          <div className="row subtitles">
            <div className="col-xs-3 output-data">
              <div className="row">
                <span className="output-without-unit">ENTERING SUPPLY AIR</span>
              </div>
            </div>
            <div className="col-xs-3 col-xs-offset-3 output-data">
              <div className="row">
                <span className="output-without-unit">ENTERING EXHAUST AIR</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-xs-4 label-part right-row" data-name={`oa_air_stream_${s}_id`} data-name_2={`ra_air_stream_${s}_id`}>
          Air Stream
        </div>
        <div className="col-xs-8 input-part">
          <div className="row">
            <div className="col-xs-6">
              <ROText
                className={`${SEARCH_FIELD} text-left-float-right`}
                value={m[`oa_air_stream_${s}`]}
              />
            </div>
            <div className="col-xs-6 text-right">
              <ROText
                className={`${SEARCH_FIELD} text-left-float-right`}
                value={m[`ra_air_stream_${s}`]}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-xs-4 label-part right-row" data-name={`oa_airflow_actual_${s}`} data-name_2={`ra_airflow_actual_${s}`}>
          Airflow
        </div>
        <div className="col-xs-8 input-part">
          <div className="row">
            <div className="col-xs-6">
              <span className="percent">CFM</span>
              <input className={SEARCH_FIELD} type="text" data-type="number" readOnly tabIndex={-1} value={hbStr(m[`oa_airflow_actual_${s}`])} />
            </div>
            <div className="col-xs-6">
              <span className={w ? 'unit' : 'percent'}>CFM</span>
              <input className={SEARCH_FIELD} type="text" data-type="number" readOnly tabIndex={-1} value={hbStr(m[`ra_airflow_actual_${s}`])} />
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-xs-4 label-part right-row" data-name={`oa_db_temp_${s}`} data-name_2={`ra_db_temp_${s}`}>
          DB
        </div>
        <div className={w ? 'col-xs-8 input-part four-input-row' : 'col-xs-8 four-input-row input-part'}>
          <div className="row">
            <div className="col-xs-6">
              <span className={w ? 'temp unit' : 'left percent temp'}>F</span>
              <input className={SEARCH_FIELD} type="text" data-type="number" readOnly tabIndex={-1} value={hbStr(m[`oa_db_temp_${s}`])} />
            </div>
            <div className="col-xs-6">
              <span className={w ? 'unit temp' : 'left percent temp'}>F</span>
              <input className={SEARCH_FIELD} type="text" data-type="number" readOnly tabIndex={-1} value={hbStr(m[`ra_db_temp_${s}`])} />
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-xs-4 label-part right-row" data-name={`oa_rh_${s}`} data-name_2={`ra_rh_${s}`}>
          Humidity
        </div>
        <div className="col-xs-8 four-input-row input-part">
          <div className="row">
            <div className="col-xs-6">
              <span className={w ? 'unit' : 'left percent'}>%</span>
              <input className={SEARCH_FIELD} type="text" data-type="number" readOnly tabIndex={-1} value={hbStr(m[`oa_rh_${s}`])} />
            </div>
            <div className="col-xs-6">
              <span className={w ? 'unit' : 'left percent'}>%</span>
              <input className={SEARCH_FIELD} type="text" data-type="number" readOnly tabIndex={-1} value={hbStr(m[`ra_rh_${s}`])} />
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-xs-4 label-part right-row" data-name="oa_sp" data-name_2="ra_sp">
          Static Pressure
        </div>
        <div className="col-xs-8 four-input-row input-part">
          <div className="row">
            <div className="col-xs-6">
              <span className={w ? 'unit' : 'percent left'}>in WC</span>
              <input className={SEARCH_FIELD} type="text" data-type="number" readOnly tabIndex={-1} value={hbStr(m.oa_sp)} />
            </div>
            <div className="col-xs-6">
              <span className={w ? 'unit' : 'percent left'}>in WC</span>
              <input className={SEARCH_FIELD} type="text" data-type="number" readOnly tabIndex={-1} value={hbStr(m.ra_sp)} />
            </div>
          </div>
        </div>
      </div>
      {w ? (
        <div className="row top-margin">
          <div className="col-xs-4 label-part right-row" data-name="eatr_oacf">
            EATR-OACF -
          </div>
          <div className="col-xs-8  four-input-row">
            <div className="row">
              <div className="col-xs-12">
                <div className="row">
                  <div className="col-xs-6">
                    <input type="checkbox" tabIndex={35} name="eatr_oacf" checked={hbIf(m.eatr_oacf)} readOnly />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="row top-margin">
          <div className="col-xs-4 label-part right-row" data-name="eatr_oacf">
            EATR-OACF -
          </div>
          <div className="col-xs-8">
            <div className="row">
              <div className="col-xs-6">
                {/* {{#if model.eatr_oacf}} — `model` is not a field of the
                    row → never checked on the live board (tmpl:429). */}
                <input type="checkbox" checked={hbIf(sub(m, 'model').eatr_oacf)} readOnly tabIndex={22} name="eatr_oacf" className="search_field" />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/** Output Performance season block (tmpl:744-1152 summer / 1153-1608 winter). */
const SeasonOutputBlock = ({ m, mw, season }: { m: Row; mw: Row; season: 'summer' | 'winter' }) => {
  const s = season;
  const w = season === 'winter';
  return (
    <>
      <div className="col-xs-12 col-md-12 col-lg-7">
        <div className="row">
          <div className="col-xs-offset-4 col-xs-8 input-part">
            <div className="row subtitles">
              <div className="col-xs-3 output-data">
                <div className="row">
                  <span className="output-without-unit">LEAVING SUPPLY AIR</span>
                </div>
              </div>
              <div className="col-xs-3 col-xs-offset-3 output-data">
                <div className="row">
                  <span className="output-without-unit">LEAVING EXHAUST AIR</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <OutRow label="Airstream">
          <div className="col-xs-3 output-data">
            <div className="row">
              <NumOut value={m[`oa_air_stream_${s}`]} name={`oa_air_stream_${s}_id`} cls="number_output output-without-unit" />
            </div>
          </div>
          <div className="col-xs-3 col-xs-offset-3 output-data">
            <div className="row">
              <NumOut value={m[`ra_air_stream_${s}`]} name={`ra_air_stream_${s}_id`} cls="number_output output-without-unit" />
            </div>
          </div>
        </OutRow>
        <OutRow label="Airflow">
          <div className="col-xs-3 output-data">
            <div className="row">
              <span className="percent">CFM</span>
              <NumOut value={m[`oa_airflow_actual_${s}`]} fmt="integer" name={`oa_airflow_actual_${s}`} />
            </div>
          </div>
          <div className="col-xs-3 col-xs-offset-3 output-data">
            <div className="row">
              <span className="percent">CFM</span>
              <NumOut value={m[`ra_airflow_actual_${s}`]} fmt="integer" name={`ra_airflow_actual_${s}`} />
            </div>
          </div>
        </OutRow>
        <OutRow label="DB">
          <div className="col-xs-3 output-data">
            <div className="row">
              <span className="percent temp">F</span>
              <NumOut value={m[`sa_db_temp_${s}`]} fmt="decimal" name={`sa_db_temp_${s}`} />
            </div>
          </div>
          <div className="col-xs-3 col-xs-offset-3 output-data">
            <div className="row">
              <span className="percent temp">F</span>
              <NumOut value={m[`ea_db_temp_${s}`]} fmt="decimal" name={`ea_db_temp_${s}`} />
            </div>
          </div>
        </OutRow>
        <OutRow label="Humidity">
          <div className="col-xs-3 output-data">
            <div className="row">
              <span className="percent">gr/lb</span>
              {/* summer sa_hr has NO formatter attr; winter HAS decimal
                  (tmpl:861-865 vs :1266-1274) */}
              <NumOut value={m[`sa_hr_${s}`]} fmt={w ? 'decimal' : undefined} name={`sa_hr_${s}`} />
            </div>
          </div>
          <div className="col-xs-3 output-data">
            <div className="row">
              <span className="percent unit-between">%</span>
              <NumOut value={m[`sa_rh_${s}`]} fmt="decimal" name={`sa_rh_${s}`} />
            </div>
          </div>
          <div className="col-xs-3 output-data">
            <div className="row">
              <span className="percent">gr/lb</span>
              {/* same asymmetry for ea_hr (tmpl:879-887 vs :1288-1297) */}
              <NumOut value={m[`ea_hr_${s}`]} fmt={w ? 'decimal' : undefined} name={`ea_hr_${s}`} />
            </div>
          </div>
          <div className="col-xs-3 output-data">
            <div className="row">
              <span className="percent unit-between">%</span>
              <NumOut value={m[`ea_rh_${s}`]} fmt="decimal" name={`ea_rh_${s}`} />
            </div>
          </div>
        </OutRow>
        <OutRow label="Enthalpy">
          <div className="col-xs-3 output-data">
            <div className="row">
              <span className="percent">btu/lb</span>
              <NumOut value={m[`sa_w_${s}`]} fmt="decimal" name={`sa_w_${s}`} cls="long-input number_output" />
            </div>
          </div>
          <div className="col-xs-3 col-xs-offset-3 output-data">
            <div className="row">
              <span className="percent">btu/lb</span>
              <NumOut value={m[`ea_w_${s}`]} fmt="decimal" name={`ea_w_${s}`} cls="long-input number_output" />
            </div>
          </div>
        </OutRow>
        <OutRow label={<div className="">Face Velocity</div>}>
          <div className=" output-data">
            <div className="col-xs-3">
              <div className="row">
                <span className="percent">fpm</span>
                <NumOut value={m[`sa_face_velocity_${s}`]} fmt="integer" name={`sa_face_velocity_${s}`} />
              </div>
            </div>
            <div className="col-xs-3 col-xs-offset-3">
              <div className="row">
                <span className="percent">{w ? ' fpm ' : 'fpm'}</span>
                <NumOut value={m[`ea_face_velocity_${s}`]} fmt="integer" name={`ea_face_velocity_${s}`} />
              </div>
            </div>
          </div>
        </OutRow>
        <OutRow label={<div className="">Air PD </div>}>
          <div className=" output-data">
            <div className="col-xs-3">
              <div className="row">
                <span className="percent"> in WC </span>
                <NumOut value={m[`sa_apd_${s}`]} fmt="decimal" name={`sa_apd_${s}`} />
              </div>
            </div>
            <div className="col-xs-3 col-xs-offset-3">
              <div className="row">
                <span className="percent"> in WC </span>
                <NumOut value={m[`ea_apd_${s}`]} fmt="decimal" name={`ea_apd_${s}`} />
              </div>
            </div>
          </div>
        </OutRow>
      </div>
      <div className="col-xs-12 col-md-12 col-lg-5 top-margin">
        <CapacityRow
          label={<span>Sensible</span>}
          capValue={m[`sensible_capacity_${s}`]}
          capName={`sensible_capacity_${s}`}
          effValue={m[`sensible_efficiency_${s}`]}
          effName={`sensible_efficiency_${s}`}
        />
        <CapacityRow
          label="Latent"
          // legacy summer Latent row renders total_capacity_summer
          // (tmpl:1040-1046); winter renders latent_capacity_winter with
          // data-name="ladb" (tmpl:1453-1459)
          capValue={w ? m.latent_capacity_winter : m.total_capacity_summer}
          capName={w ? 'ladb' : 'total_capacity_summer'}
          effValue={m[`latent_efficiency_${s}`]}
          effName={w ? 'ladb' : `latent_efficiency_${s}`}
        />
        <CapacityRow
          label="Total"
          capValue={m[`total_capacity_${s}`]}
          capName={`total_capacity_${s}`}
          effValue={m[`total_efficiency_${s}`]}
          effName={`total_efficiency_${s}`}
        />
        <div className="row">
          <div className="col-xs-5 label-part">
            <div className="">EATR | OACF</div>
          </div>
          <div className="col-xs-7 input-part">
            <div className="row">
              <div className=" output-data">
                <div className="col-xs-6">
                  <div className="row">
                    <NumOut value={m[`eatr_${s}`]} fmt="decimal" name={`eatr_${s}`} cls="number_output output-without-unit" />
                  </div>
                </div>
                <div className="col-xs-6">
                  <div className="row">
                    <span className="percent" />
                    <NumOut
                      value={m[`oacf_${s}`]}
                      fmt="decimal"
                      name={`oacf_${s}`}
                      cls={w ? 'number_output output-without-unit' : 'number_output'}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-xs-5 label-part">
            <div className="">OACF Airflow</div>
          </div>
          <div className="col-xs-7 input-part">
            <div className="row">
              <div className=" output-data">
                <div className="col-xs-6">
                  <div className="row">
                    <span className="percent">CFM</span>
                    <NumOut value={m.oacf} fmt="decimal" name="oacf" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {w && (
          <>
            <div className="row">
              <div className="col-xs-5 label-part">
                <div className="right-row">Condensate</div>
              </div>
              <div className="col-xs-7 input-part">
                <div className="row">
                  <div className=" output-data">
                    <div className="col-xs-6">
                      <div className="row">
                        <span className="percent">gpm</span>
                        <NumOut value={m.condensate_winter} fmt="integer" name="condensate_winter" cls="medium-input number_output" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-xs-5 label-part">
                <div className="right-row" data-name="frost_control_winter">
                  Frost Control
                </div>
              </div>
              <div className="col-xs-7 input-part">
                <div className="row output-data">
                  <div className="col-xs-6">
                    <div className="row">
                      <span className="output-without-unit" data-name="frost_control_winter">
                        {hbIf(mw.frost_control_winter) ? 'Yes ' : 'No '}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

/** Sensible/Latent/Total capacity+efficiency row (efficiency only when the
 *  value is Handlebars-truthy — note 0 IS truthy in Handlebars). */
const CapacityRow = ({
  label,
  capValue,
  capName,
  effValue,
  effName,
}: {
  label: ReactNode;
  capValue: unknown;
  capName: string;
  effValue: unknown;
  effName: string;
}) => (
  <div className="row">
    <div className="col-xs-5 label-part">
      <div className="right-row">{label}</div>
    </div>
    <div className="col-xs-7 input-part">
      <div className="row">
        <div className=" output-data">
          <div className="col-xs-6">
            <div className="row">
              <span className="percent">btu/hr</span>
              <NumOut value={capValue} fmt="integer" name={capName} cls=" number_output" />
            </div>
          </div>
          {hbIf(effValue) && (
            <div className="col-xs-6">
              <div className="row">
                <span className="percent">%</span>
                <NumOut value={effValue} fmt="decimal" name={effName} cls=" number_output" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

export default EwheelSelectionTab;
